import React, { memo } from 'react';
import { formatCurrency } from '../helpers';

const TASAS_IVA = [
    { label: '21.0% (General)', value: 21 },
    { label: '10.5% (Reducida)', value: 10.5 },
    { label: '27.0% (Servicios)', value: 27 },
    { label: '0.0% (Exento)', value: 0 }
];

const SeccionCalculoFactura = ({
    formData,
    setFormData,
    calculos,
    esFacturaB,
    handleBlurCurrency
}) => {
    const esComprobante = formData.tipoComprobante === 'Comprobante';
    const esMontoTotalDirecto = esFacturaB || esComprobante;

    return (
        <fieldset className="border border-slate-200 rounded-xl p-6 relative bg-white shadow-sm transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-slate-700 bg-white absolute -top-3.5 left-4 tracking-tight">
                Cálculo de Importes e Impuestos
            </legend>
            
            <div className="space-y-5 pt-2">
                {/* 🟢 ENTRADA DE MONTO TOTAL/BRUTO (FACTURA B O COMPROBANTE) */}
                {esMontoTotalDirecto ? (
                    <div className="space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                            <label className="block text-[14px] font-bold text-emerald-800 pl-1">
                                {esComprobante ? 'Monto Total / Bruto *' : 'Monto Total / Bruto (Factura B) *'}
                            </label>

                            {/* 🟢 CHECKBOX EXCLUSIVO PARA FACTURA B EXENTA */}
                            {esFacturaB && (
                                <label className="inline-flex items-center gap-2 cursor-pointer select-none bg-emerald-50/60 border border-emerald-200 px-3 py-1 rounded-xl transition-all hover:bg-emerald-100/50">
                                    <input
                                        type="checkbox"
                                        checked={!!formData.esExentoIva}
                                        onChange={e => setFormData(prev => ({ ...prev, esExentoIva: e.target.checked }))}
                                        className="w-4 h-4 text-emerald-600 rounded focus:ring-emerald-400 border-slate-300 cursor-pointer"
                                    />
                                    <span className="text-xs font-bold text-emerald-900 uppercase">
                                        Operación Exenta / Sin IVA
                                    </span>
                                </label>
                            )}
                        </div>

                        <div className="flex gap-2">
                            <select
                                value={formData.moneda === 'USD' ? 'USD U$S' : 'ARS $'}
                                onChange={e => {
                                    const val = e.target.value.includes('USD') ? 'USD' : 'ARS';
                                    setFormData(prev => ({ ...prev, moneda: val }));
                                }}
                                className="w-32 border border-emerald-300 bg-emerald-50/30 rounded-xl px-3 py-2.5 text-sm font-bold text-emerald-950 outline-none h-[46px] focus:border-emerald-500 cursor-pointer"
                            >
                                <option value="ARS $">ARS $</option>
                                <option value="USD U$S">USD U$S</option>
                            </select>

                            <input
                                type="text"
                                value={formData.montoTotalInput || ''}
                                onChange={e => setFormData(prev => ({ ...prev, montoTotalInput: e.target.value }))}
                                onBlur={() => handleBlurCurrency('montoTotalInput')}
                                className="flex-grow border border-emerald-300 bg-emerald-50/20 rounded-xl px-4 py-2.5 text-lg font-mono font-black text-emerald-950 outline-none h-[46px] focus:border-emerald-500 focus:ring-1 focus:ring-emerald-200 transition-all"
                                placeholder="0,00"
                                required
                            />
                        </div>

                        <span className="text-[10px] text-slate-400 font-bold uppercase block pl-1">
                            {esComprobante 
                                ? 'Documento Interno / Sin IVA (Monto Total = Monto Neto)' 
                                : formData.esExentoIva
                                    ? 'Factura B Exenta de IVA (Monto Total = Monto Neto)'
                                    : 'Consumidor Final / Exento (IVA 21% Desglosado Internamente para Auditoría)'}
                        </span>
                    </div>
                ) : (
                    /* 🔵 ENTRADA ESTÁNDAR (FACTURA A, C, ETC.) */
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Monto Neto *</label>
                            <div className="flex gap-2">
                                <select
                                    value={formData.moneda === 'USD' ? 'USD U$S' : 'ARS $'}
                                    onChange={e => {
                                        const val = e.target.value.includes('USD') ? 'USD' : 'ARS';
                                        setFormData(prev => ({ ...prev, moneda: val }));
                                    }}
                                    className="w-32 border border-slate-200 bg-slate-50 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-800 outline-none h-[46px] focus:border-blue-400 cursor-pointer"
                                >
                                    <option value="ARS $">ARS $</option>
                                    <option value="USD U$S">USD U$S</option>
                                </select>

                                <input
                                    type="text"
                                    value={formData.montoNeto || ''}
                                    onChange={e => setFormData(prev => ({ ...prev, montoNeto: e.target.value }))}
                                    onBlur={() => handleBlurCurrency('montoNeto')}
                                    className="flex-grow border border-slate-200 rounded-xl px-4 py-2.5 text-base font-mono font-bold text-slate-900 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all"
                                    placeholder="0,00"
                                    required
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-[14px] font-bold text-slate-800 mb-1.5 pl-1">Tasa de IVA (%)</label>
                            <select 
                                value={formData.tasaIva} 
                                onChange={e => setFormData(prev => ({ ...prev, tasaIva: e.target.value }))} 
                                className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-800 outline-none bg-white h-[46px] focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all cursor-pointer"
                            >
                                {TASAS_IVA.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                            </select>
                        </div>
                    </div>
                )}

                {/* TARJETAS INFORMATIVAS INFERIORES */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                    <div className="bg-blue-50/70 p-4 rounded-xl border border-blue-300 flex flex-col justify-center">
                        <span className="text-[10px] font-black text-blue-800 uppercase tracking-wider">
                            {esComprobante || formData.esExentoIva 
                                ? 'Monto IVA (Exento / 0%)' 
                                : `Monto IVA (${calculos.tasa}%)`}
                        </span>
                        <span className="font-mono text-lg font-black text-blue-800 mt-0.5">
                            {formatCurrency(calculos.montoIva, calculos.monedaSimbolo)}
                        </span>
                    </div>

                    <div className="bg-emerald-50/70 text-emerald-800 p-4 rounded-xl border border-emerald-200 flex flex-col justify-center shadow-xs">
                        <span className="text-[10px] font-black uppercase tracking-wider opacity-90">
                            {esComprobante ? 'Monto Total (Comprobante)' : 'Monto Total (Factura)'}
                        </span>
                        <span className="font-mono text-lg font-black mt-0.5">
                            {formatCurrency(calculos.montoTotal, calculos.monedaSimbolo)}
                        </span>
                    </div>
                </div>
            </div>
        </fieldset>
    );
};

export default memo(SeccionCalculoFactura);